// Punktlista — the small inline icons. One 16-grid, one stroke width, so a
// glyph next to text sits on the text's baseline and reads as part of it.
// Its own module (not part of Delivery.jsx) because both the digest
// component and the shell's account menu use it, and those two must not
// import each other.

import React from 'react';

export function Glyph({ name, size = 14 }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    focusable: false,
  };
  if (name === 'arrow') {
    return (
      <svg {...common}>
        <path d="M3 8h10M9 4l4 4-4 4" />
      </svg>
    );
  }
  if (name === 'check') {
    return (
      <svg {...common}>
        <path d="M3 8.5l3.5 3.5L13 5" />
      </svg>
    );
  }
  if (name === 'lock') {
    return (
      <svg {...common}>
        <rect x="3.5" y="7" width="9" height="6.5" rx="1.5" />
        <path d="M5.5 7V5.5a2.5 2.5 0 0 1 5 0V7" />
      </svg>
    );
  }
  if (name === 'plus') {
    return (
      <svg {...common}>
        <path d="M8 3v10M3 8h10" />
      </svg>
    );
  }
  if (name === 'cross') {
    return (
      <svg {...common}>
        <path d="M4 4l8 8M12 4l-8 8" />
      </svg>
    );
  }
  if (name === 'chevron') {
    return (
      <svg {...common}>
        <path d="M4 6l4 4 4-4" />
      </svg>
    );
  }
  // The four below belong to the account menu in Shell.jsx: an envelope for
  // Mina utskick, an eye for Bevakningar, a card for Din plan and a door
  // with an arrow for Logga ut. Same 16-grid and stroke as the rest so they
  // sit on one baseline with the text next to them.
  if (name === 'mail') {
    return (
      <svg {...common}>
        <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
        <path d="M2.5 4.5L8 9l5.5-4.5" />
      </svg>
    );
  }
  if (name === 'eye') {
    return (
      <svg {...common}>
        <path d="M1.5 8s2.5-4.5 6.5-4.5S14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z" />
        <circle cx="8" cy="8" r="2" />
      </svg>
    );
  }
  if (name === 'card') {
    return (
      <svg {...common}>
        <rect x="2" y="4" width="12" height="8.5" rx="1.5" />
        <path d="M2 7h12M4.5 10h3" />
      </svg>
    );
  }
  if (name === 'logout') {
    return (
      <svg {...common}>
        <path d="M6.5 2.5H3.5a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h3" />
        <path d="M9 5l3 3-3 3M12 8H6" />
      </svg>
    );
  }
  // The seven below are the source kinds (RAPPORTVAKT_KINDS in
  // src/config/rapportvakt.js): the small mark a source row shows next to its
  // publisher logo, or in its place when there is none. Same 16-grid and
  // stroke as the rest, one colour (currentColor), no fill except the two
  // spots (the video glyph's play mark, the social glyph's dot) small enough
  // that an outline would just read as noise.
  if (name === 'report') {
    return (
      <svg {...common}>
        <path d="M4.5 2h4.5l2.5 2.5V14h-7z" />
        <path d="M9 2v2.5h2.5" />
        <path d="M6 8.5h4M6 11h4" />
      </svg>
    );
  }
  if (name === 'news') {
    return (
      <svg {...common}>
        <rect x="2" y="3.5" width="12" height="9" rx="1" />
        <path d="M4.5 6h3.5v3H4.5zM4.5 11h7M9.5 6.5h3M9.5 8.5h3" />
      </svg>
    );
  }
  if (name === 'blog') {
    return (
      <svg {...common}>
        <path d="M3 13l0.8-3 7-7 2.2 2.2-7 7-3 0.8z" />
        <path d="M9.3 3.7l2.2 2.2" />
      </svg>
    );
  }
  if (name === 'video') {
    return (
      <svg {...common}>
        <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
        <path d="M6.7 6.2v3.6l3.3-1.8z" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  if (name === 'social') {
    return (
      <svg {...common}>
        <path d="M2.5 4.5h11v6.5h-6.2L4.5 13v-2H2.5z" />
        <circle cx="8" cy="7.7" r="0.6" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  if (name === 'page') {
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="6" />
        <path d="M2 8h12M8 2c1.8 1.7 1.8 10.3 0 12M8 2c-1.8 1.7-1.8 10.3 0 12" />
      </svg>
    );
  }
  // The three below are for RAPPORTVAKT_UPCOMING_KINDS (src/config/
  // rapportvakt.js): kinds shown on the sources tab as "coming soon", not yet
  // readable by the backend. Same 16-grid, stroke and one colour as the rest
  // — 'newsletter' in that list reuses 'mail' above rather than a new glyph.
  if (name === 'x') {
    return (
      <svg {...common}>
        <path d="M2.5 2.5l11 11M13.5 2.5l-11 11" />
      </svg>
    );
  }
  if (name === 'linkedin') {
    return (
      <svg {...common}>
        <rect x="2" y="2" width="12" height="12" rx="2.5" />
        <path d="M5.3 6.8V12" />
        <circle cx="5.3" cy="4.6" r="0.9" fill="currentColor" stroke="none" />
        <path d="M8.5 12V8.3M8.5 9.4a1.7 1.7 0 0 1 3.4 0V12" />
      </svg>
    );
  }
  if (name === 'podcast') {
    return (
      <svg {...common}>
        <rect x="5.5" y="2" width="5" height="8" rx="2.5" />
        <path d="M3.5 8a4.5 4.5 0 0 0 9 0" />
        <path d="M8 12.5V14M6 14h4" />
      </svg>
    );
  }
  return null;
}
